import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, Download, Upload } from "lucide-react";
import { useIssues, useStats, useUrlsSummary } from "@/hooks/useValidation";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import { FilterBar } from "@/components/validation/FilterBar";
import { StatsDashboard } from "@/components/validation/StatsDashboard";
import { IssuesTable } from "@/components/validation/IssuesTable";
import { GroupedIssuesTable } from "@/components/validation/GroupedIssuesTable";
import { IssueDetailsDialog } from "@/components/validation/IssueDetailsDialog";
import { UrlScrapeDialog } from "@/components/validation/UrlScrapeDialog";
import { SourceScrapeDialog } from "@/components/validation/SourceScrapeDialog";
import { JobResultModal } from "@/components/jobs/JobResultModal";
import { UrlSummaryTable } from "@/components/validation/UrlSummaryTable";
import { IssuesBulkResolveDialog } from "@/components/validation/IssuesBulkResolveDialog";
import { OverrideFormDialog } from "@/components/overrides/OverrideFormDialog";
import type { FieldName } from "@/types/overrides";
import { FIELD_NAME_OPTIONS } from "@/types/overrides";
import { Pagination } from "@/components/ui/pagination";
import type { ValidationFilters, ValidationIssue } from "@/types/validation";
import { Toaster } from "sonner";
import { useUrlState } from "@/hooks/useUrlState";
import { useSessionState } from "@/hooks/useSessionState";
import type {
  ValidationUrlState,
  ValidationSessionState,
} from "@/types/pageState";
import { serializeSet, deserializeSet } from "@/lib/stateUtils";

const ITEMS_PER_PAGE = 20;

/** Issue pipeline ids not returned by `GET /api/scrapers/sources` (same strings Review uses for scraper `source`). */
const VALIDATION_SOURCE_EXTRAS: { id: string; label: string }[] = [
  { id: "automated", label: "Automated" },
  { id: "llm_verification", label: "LLM Verification" },
  { id: "agent_verification", label: "Agent Verification" },
];

export default function Validation() {
  // URL state for filters, pagination, and tab
  const [urlState, setUrlState] = useUrlState<ValidationUrlState>({
    schema: {
      source: "string",
      level: "string",
      status: "string",
      scope: "string",
      validation_type: "string",
      search: "string",
      url: "string",
      product_id: "string",
      variant_id: "string",
      page: "number",
      tab: "string",
    },
    defaults: {
      status: "unresolved",
      page: 1,
      tab: "all-issues",
    },
    replace: true,
    debounceMs: 300, // Debounce search/url inputs
  });

  // Session state for dialogs and expanded state
  const [sessionState, setSessionState] =
    useSessionState<ValidationSessionState>({
      key: "validation-ui",
      defaultValue: {
        detailsDialogOpen: false,
        urlScrapeDialogOpen: false,
        sourceScrapeDialogOpen: false,
        resultModalOpen: false,
        expandedRows: new Set<string>(),
        expandedGroups: new Set<string>(),
      },
      serialize: (state) =>
        JSON.stringify({
          ...state,
          expandedRows: serializeSet(state.expandedRows),
          expandedGroups: serializeSet(state.expandedGroups),
        }),
      deserialize: (str) => {
        const parsed = JSON.parse(str);
        return {
          ...parsed,
          expandedRows: deserializeSet(parsed.expandedRows),
          expandedGroups: deserializeSet(parsed.expandedGroups),
        };
      },
    });

  // Transient state (not persisted)
  const [selectedIssue, setSelectedIssue] = useState<ValidationIssue | null>(
    null,
  );
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<number>>(
    new Set(),
  );
  const [bulkResolveOpen, setBulkResolveOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideInitialValues, setOverrideInitialValues] = useState<
    | {
        source: string;
        external_variant_id: string;
        field_name: FieldName | "";
        notes: string;
      }
    | undefined
  >(undefined);

  // Derive API filters from URL state
  const filters = useMemo<ValidationFilters>(() => {
    const f: ValidationFilters = {
      limit: ITEMS_PER_PAGE,
      offset: ((urlState.page || 1) - 1) * ITEMS_PER_PAGE,
    };
    if (urlState.source) f.source = urlState.source;
    if (urlState.level) f.level = urlState.level as "error" | "warning";
    if (urlState.status)
      f.status = urlState.status as "unresolved" | "addressed";
    if (urlState.scope) f.scope = urlState.scope as "product" | "variant";
    if (urlState.validation_type) f.validation_type = urlState.validation_type;
    if (urlState.search) f.search = urlState.search;
    if (urlState.url) f.url = urlState.url;
    if (urlState.product_id) f.product_id = urlState.product_id;
    if (urlState.variant_id) f.variant_id = urlState.variant_id;
    return f;
  }, [urlState]);

  const { data: scraperSourceIds = [], isLoading: sourcesLoading } =
    useScrapersSources();

  const sourceFilterOptions = useMemo(() => {
    const fromScrapers = scraperSourceIds.map((id) => ({ id, label: id }));
    const ids = new Set(fromScrapers.map((s) => s.id));
    const extras = VALIDATION_SOURCE_EXTRAS.filter((e) => !ids.has(e.id));
    const sortedScrapers = [...fromScrapers].sort((a, b) =>
      a.id.localeCompare(b.id),
    );
    return [...extras, ...sortedScrapers];
  }, [scraperSourceIds]);

  // Queries
  const {
    data: issues = [],
    isLoading: issuesLoading,
    refetch: refetchIssues,
  } = useIssues(filters);
  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useStats(filters);

  const urlsSummaryParams: { limit: number; offset: number; source?: string } =
    {
      limit: ITEMS_PER_PAGE,
      offset: filters.offset || 0,
    };
  if (filters.source) {
    urlsSummaryParams.source = filters.source;
  }
  const {
    data: urlsSummary = [],
    isLoading: urlsLoading,
    refetch: refetchUrls,
  } = useUrlsSummary(urlsSummaryParams);

  const handleFiltersChange = (newFilters: ValidationFilters) => {
    setUrlState((prev) => {
      // Apply all filter keys from newFilters so that missing/undefined values clear URL state
      const update: Partial<ValidationUrlState> = { ...prev, page: 1 };
      const filterKeys: (keyof ValidationFilters)[] = [
        "source",
        "level",
        "status",
        "scope",
        "validation_type",
        "search",
        "url",
        "product_id",
        "variant_id",
      ];
      for (const key of filterKeys) {
        const v = newFilters[key];
        if (v !== undefined) {
          (update as Record<string, string | number>)[key] = v as string &
            number;
        } else {
          (update as Record<string, unknown>)[key] = undefined;
        }
      }
      return update;
    });
  };

  const handlePageChange = (page: number) => {
    setUrlState((prev) => ({ ...prev, page }));
  };

  const handleRefresh = () => {
    refetchIssues();
    refetchStats();
    refetchUrls();
  };

  const handleResolveIssue = (issue: ValidationIssue) => {
    setSelectedIssue(issue);
    setSessionState({ ...sessionState, detailsDialogOpen: true });
  };

  const handleViewDetails = (issue: ValidationIssue) => {
    setSelectedIssue(issue);
    setSessionState({ ...sessionState, detailsDialogOpen: true });
  };

  const handleScrapeUrl = (url: string) => {
    setScrapeUrl(url);
    setSessionState({ ...sessionState, urlScrapeDialogOpen: true });
  };

  const handleViewUrlIssues = (url: string) => {
    setUrlState((prev) => ({ ...prev, url, page: 1, tab: "all-issues" }));
  };

  const validFieldNames = new Set(FIELD_NAME_OPTIONS.map((o) => o.value));

  const handleCreateOverride = (issue: ValidationIssue) => {
    setOverrideInitialValues({
      source: issue.scraper_source ?? "",
      external_variant_id: issue.external_variant_id ?? "",
      field_name:
        issue.field_name && validFieldNames.has(issue.field_name as FieldName)
          ? (issue.field_name as FieldName)
          : "",
      notes: issue.message,
    });
    setOverrideDialogOpen(true);
  };

  const selectedIssues = useMemo(
    () => issues.filter((issue) => selectedIssueIds.has(issue.id)),
    [issues, selectedIssueIds],
  );

  return (
    <div className="space-y-6">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Validation</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setSessionState({ ...sessionState, urlScrapeDialogOpen: true })
            }
          >
            <Download className="w-4 h-4" />
            Scrape URL
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setSessionState({ ...sessionState, sourceScrapeDialogOpen: true })
            }
          >
            <Upload className="w-4 h-4" />
            Scrape Source
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Stats Dashboard */}
        <StatsDashboard stats={stats!} isLoading={statsLoading} />

        {/* Filter Bar */}
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <FilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              stats={stats}
              sourceOptions={sourceFilterOptions}
              sourcesLoading={sourcesLoading}
            />
          </CardContent>
        </Card>

        {/* Tabs for different views */}
        <Tabs
          value={urlState.tab || "all-issues"}
          onValueChange={(tab) => setUrlState((prev) => ({ ...prev, tab }))}
        >
          <TabsList>
            <TabsTrigger value="all-issues">All Issues</TabsTrigger>
            <TabsTrigger value="grouped">Grouped</TabsTrigger>
            <TabsTrigger value="by-url">By URL</TabsTrigger>
          </TabsList>

          <TabsContent value="all-issues" className="space-y-4">
            {selectedIssues.length > 0 && (
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  Selected {selectedIssues.length} issue
                  {selectedIssues.length === 1 ? "" : "s"}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedIssueIds(new Set())}
                  >
                    Clear selection
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setBulkResolveOpen(true)}
                    disabled={selectedIssues.every(
                      (i) => i.status !== "unresolved",
                    )}
                  >
                    Resolve selected
                  </Button>
                </div>
              </div>
            )}
            <IssuesTable
              issues={issues}
              isLoading={issuesLoading}
              onResolve={handleResolveIssue}
              onViewDetails={handleViewDetails}
              onScrapeUrl={handleScrapeUrl}
              onCreateOverride={handleCreateOverride}
              expandedRows={sessionState.expandedRows}
              onExpandedRowsChange={(rows) =>
                setSessionState({ ...sessionState, expandedRows: rows })
              }
              selectedIssueIds={selectedIssueIds}
              onSelectedIssueIdsChange={setSelectedIssueIds}
            />
            {issues.length > 0 && (
              <Pagination
                currentPage={urlState.page || 1}
                totalItems={stats?.total_issues || issues.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}
          </TabsContent>

          <TabsContent value="grouped" className="space-y-4">
            <GroupedIssuesTable
              issues={issues}
              isLoading={issuesLoading}
              onResolve={handleResolveIssue}
              onViewDetails={handleViewDetails}
              onScrapeUrl={handleScrapeUrl}
              expandedGroups={sessionState.expandedGroups}
              onExpandedGroupsChange={(groups) =>
                setSessionState({ ...sessionState, expandedGroups: groups })
              }
            />
            {issues.length > 0 && (
              <Pagination
                currentPage={urlState.page || 1}
                totalItems={stats?.total_issues || issues.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}
          </TabsContent>

          <TabsContent value="by-url" className="space-y-4">
            <UrlSummaryTable
              urls={urlsSummary}
              isLoading={urlsLoading}
              onViewUrl={handleViewUrlIssues}
              onScrapeUrl={handleScrapeUrl}
            />
            {urlsSummary.length > 0 && (
              <Pagination
                currentPage={urlState.page || 1}
                totalItems={urlsSummary.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <IssueDetailsDialog
        issue={selectedIssue}
        open={sessionState.detailsDialogOpen}
        onOpenChange={(open) =>
          setSessionState({ ...sessionState, detailsDialogOpen: open })
        }
        onScrapeUrl={handleScrapeUrl}
      />
      <IssuesBulkResolveDialog
        issues={selectedIssues}
        open={bulkResolveOpen}
        onOpenChange={setBulkResolveOpen}
        onResolved={() => setSelectedIssueIds(new Set())}
      />
      <UrlScrapeDialog
        open={sessionState.urlScrapeDialogOpen}
        onOpenChange={(open) =>
          setSessionState({ ...sessionState, urlScrapeDialogOpen: open })
        }
        initialUrl={scrapeUrl}
        onViewRunResults={(jobId) =>
          setSessionState((prev) => ({
            ...prev,
            resultJobId: jobId,
            resultModalOpen: true,
            urlScrapeDialogOpen: false,
          }))
        }
      />
      <SourceScrapeDialog
        open={sessionState.sourceScrapeDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, sourceScrapeDialogOpen: open }))
        }
        onViewRunResults={(runId) =>
          setSessionState((prev) => ({
            ...prev,
            resultJobId: runId,
            resultModalOpen: true,
            sourceScrapeDialogOpen: false,
          }))
        }
      />
      <JobResultModal
        open={sessionState.resultModalOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, resultModalOpen: open }))
        }
        jobId={sessionState.resultJobId}
      />
      <OverrideFormDialog
        open={overrideDialogOpen}
        onOpenChange={setOverrideDialogOpen}
        {...(overrideInitialValues
          ? { initialValues: overrideInitialValues }
          : {})}
      />
    </div>
  );
}
